<template>
  <div>
    <v-row>
      <v-col
        :style="{
          height: `${leftPanelMaxScrollHeight}px`,
          'overflow-y': 'auto'
        }"
      >
        <v-card class="mb-4" height="140" width="140">
          <v-btn block height="140" :color="!activeElement ? 'blue lighten-4' : undefined" @click="openSettings">
            <v-icon>
              mdi-cog
            </v-icon>
          </v-btn>
        </v-card>
        <draggable
          v-model="treeItems"
          :group="{ name: 'lessonTree' }"
          class="draggable"
          @start="drag = true"
          @end="drag = false"
          @change="onChange"
        >
          <v-card v-for="(item, index) in items" :key="index" class="mb-4" height="140" width="140">
            <v-dialog v-model="removeDialogs[index]" persistent max-width="350">
              <template v-slot:activator="{ on }">
                <v-btn icon absolute right small class="lesson-edit-manager__top-right-btn" v-on="on">
                  <v-icon>
                    mdi-close-circle-outline
                  </v-icon>
                </v-btn>
              </template>
              <v-card>
                <v-toolbar color="error" dark flat>
                  <v-toolbar-title>Ostrzeżenie</v-toolbar-title>
                </v-toolbar>
                <v-card-text class="mt-4">
                  <p>Czy na pewno chcesz usunąć ten element?</p>
                </v-card-text>
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn color="primary" text @click="removeDialogs[index] = false">Nie</v-btn>
                  <v-btn color="primary" text @click="removeElement(index)">Tak</v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
            <v-btn
              block
              height="140"
              :color="activeElement === item ? 'blue lighten-4' : undefined"
              @click="switchElement(index)"
              >{{ index + 1 }}.
              <v-icon v-if="item.type === 'HtmlScreen'">
                mdi-card-text-outline
              </v-icon>
              <v-icon v-else-if="item.type === 'VideoScreen'">
                mdi-video
              </v-icon>
              <v-icon v-else-if="item.type === 'ImageScreen'">
                mdi-file-image-outline
              </v-icon>
              <v-icon v-else-if="item.type === 'TestScreen'">
                mdi-checkbox-multiple-marked-outline
              </v-icon>
            </v-btn>
          </v-card>
        </draggable>
        <v-card height="140" width="140">
          <v-dialog v-model="addDialog" persistent max-width="600">
            <template v-slot:activator="{ on }">
              <v-btn block height="140" v-on="on" @click="resetAddDialogState">
                <v-icon>mdi-plus-thick</v-icon>
              </v-btn>
            </template>
            <v-card>
              <v-card-title>
                <span class="headline">Dodanie nowego elementu</span>
              </v-card-title>
              <v-card-text class="mt-4">
                <p>Wybierz element z list który chcesz dodać</p>
                <p>
                  <v-select
                    v-model="selectedNewElement"
                    :items="screenTypes"
                    label="Element"
                    outlined
                    @change="uploadedSlides = []"
                  ></v-select>
                </p>
                <p v-if="selectedNewElement === 'pptx'">
                  <PptxUpload title="Dodaj plik *.pptx" @uploaded="uploadedPptx" />
                </p>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn class="text--secondary" text @click="addDialog = false">Anuluj </v-btn>
                <v-btn
                  color="primary"
                  text
                  :disabled="selectedNewElement === 'pptx' && !uploadedSlides.length"
                  @click="addNewSlide"
                  >Dodaj
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-card>
      </v-col>
      <v-col cols="10">
        <HtmlLessonEdit
          v-if="activeElement && activeElement.type === 'HtmlScreen'"
          :key="activeElementIndex"
          :lesson="activeElement"
          @change="elementChanged"
        />
        <ImageLessonEdit
          v-else-if="activeElement && activeElement.type === 'ImageScreen'"
          :key="activeElementIndex"
          :lesson="activeElement"
          @change="elementChanged"
          @disableOutsideClick="disableOutsideClick"
          @enableOutsideClick="enableOutsideClick"
        />
        <TestLessonEdit
          v-else-if="activeElement && activeElement.type === 'TestScreen'"
          :key="activeElementIndex"
          :lesson="activeElement"
          @change="elementChanged"
          @disableOutsideClick="disableOutsideClick"
          @enableOutsideClick="enableOutsideClick"
        />
        <VideoLessonEdit
          v-else-if="activeElement && activeElement.type === 'VideoScreen'"
          :key="activeElementIndex"
          :lesson="activeElement"
          @change="elementChanged"
          @disableOutsideClick="disableOutsideClick"
          @enableOutsideClick="enableOutsideClick"
        />
        <LessonBodyEdit
          v-else
          :lesson="lesson"
          :definition="newDefinition"
          :has-any-test="hasAnyTest"
          @bodyChange="bodyChange"
        />
        <slot name="manageButtons" />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import HtmlLessonEdit from '@/components/manage/courses/right-panel/lesson-edit/HtmlLessonEdit.vue';
import ImageLessonEdit from '@/components/manage/courses/right-panel/lesson-edit/ImageLessonEdit.vue';
import TestLessonEdit from '@/components/manage/courses/right-panel/lesson-edit/TestLessonEdit.vue';
import VideoLessonEdit from '@/components/manage/courses/right-panel/lesson-edit/VideoLessonEdit.vue';
import LessonBodyEdit from '@/components/manage/courses/right-panel/lesson-edit/LessonBodyEdit.vue';
import PptxUpload from '@/components/shared/PptxUpload.vue';
import draggable from 'vuedraggable';

export default {
  components: {
    HtmlLessonEdit,
    ImageLessonEdit,
    TestLessonEdit,
    VideoLessonEdit,
    LessonBodyEdit,
    PptxUpload,
    draggable
  },
  props: {
    lesson: {
      type: Object,
      required: true
    },
    definition: {
      type: Object,
      required: true
    },
    items: {
      type: Array,
      required: true
    },
    hasAnyTest: {
      type: Boolean,
      required: true
    }
  },
  data: function() {
    return {
      activeElement: null,
      activeElementIndex: null,
      validTitle: true,
      mAddDialog: false,
      removeDialogs: {},
      selectedNewElement: 'HtmlScreen',
      uploadedSlides: [],
      newDefinition: {
        name: this.lesson.name,
        displayType: this.definition.displayType,
        passLevel: this.definition.passLevel,
        isRequired: this.lesson.isRequired
      }
    };
  },
  computed: {
    addDialog: {
      get() {
        return this.mAddDialog;
      },
      set(val) {
        if (val) {
          this.$emit('disableOutsideClick');
        } else {
          this.$emit('enableOutsideClick');
        }

        this.mAddDialog = val;
      }
    },
    screenTypes() {
      return [
        { text: 'Tekst', value: 'HtmlScreen' },
        { text: 'Obraz', value: 'ImageScreen' },
        { text: 'Video', value: 'VideoScreen' },
        { text: 'Lista pytań', value: 'TestScreen' },
        { text: 'Zaimportuj plik *.pptx', value: 'pptx' }
      ];
    },
    treeItems: {
      get() {
        return this.items;
      },
      set() {
        // items are updated in store
      }
    },
    leftPanelMaxScrollHeight() {
      return window.innerHeight - 24;
    }
  },
  watch: {
    items() {
      this.$emit('slidesChanged');
    },
    removeDialogs: {
      deep: true,
      handler(dialogs) {
        for (let i in dialogs) {
          if (Object.prototype.hasOwnProperty.call(dialogs, i) && dialogs[i]) {
            this.$emit('disableOutsideClick');
            return;
          }
        }

        this.$emit('enableOutsideClick');
      }
    }
  },
  methods: {
    onChange(ev) {
      this.items = moveElement(this.items, ev.moved.oldIndex, ev.moved.newIndex);

      if (this.activeElementIndex !== ev.moved.newIndex) {
        this.activeElementIndex = ev.moved.newIndex;
        this.activeElement = this.items[this.activeElementIndex];
      }
    },
    elementChanged(ev) {
      switch (ev.type) {
        case 'HtmlScreen':
          this.items[this.activeElementIndex].html = ev.value;
          break;
        case 'ImageScreen':
          this.items[this.activeElementIndex].url = ev.value;
          break;
        case 'VideoScreen':
          this.items[this.activeElementIndex].url = ev.value;
          break;
        case 'TestScreen':
          this.items[this.activeElementIndex].questions = ev.value;
          break;
        default:
          throw 'Unsupported lesson element type';
      }

      this.$emit('update', {
        items: this.items,
        valid: this.validTitle,
        ...this.newDefinition
      });
    },
    switchElement(index) {
      this.activeElement = this.items[index];
      this.activeElementIndex = index;
    },
    bodyChange(ev) {
      this.validTitle = ev.valid;
      this.newDefinition.name = ev.name;
      /* TODO: hidden for future (#35)
      this.newDefinition.displayType = ev.displayType;*/
      this.newDefinition.passLevel = ev.passLevel;
      this.newDefinition.isRequired = ev.isRequired;
      this.$emit('update', {
        valid: ev.valid,
        name: ev.title,
        items: this.items,
        isRequired: ev.isRequired,
        ...this.newDefinition
      });
    },
    addNewSlide() {
      switch (this.selectedNewElement) {
        case 'HtmlScreen':
          this.items.push({ html: '', type: 'HtmlScreen' });
          break;
        case 'ImageScreen':
          this.items.push({ url: '', type: 'ImageScreen' });
          break;
        case 'VideoScreen':
          this.items.push({ url: '', type: 'VideoScreen' });
          break;
        case 'TestScreen':
          this.items.push({ questions: [], type: 'TestScreen' });
          break;
        case 'pptx':
          this.uploadedSlides.forEach(item => this.items.push(item));
          this.uploadedSlides = [];
          break;
        default:
          throw 'Unsupported lesson element type';
      }

      this.selectedNewElement = 'HtmlScreen';
      this.addDialog = false;
      this.activeElementIndex = this.items.length - 1;
      this.activeElement = this.items[this.activeElementIndex];
    },
    uploadedPptx(ev) {
      this.uploadedSlides = ev.slides.map(slide => ({
        url: slide.uri,
        type: 'ImageScreen'
      }));
    },
    removeElement(index) {
      this.items.splice(index, 1);
      this.removeDialogs[index] = false;
      if (this.activeElementIndex === index) {
        this.activeElementIndex = null;
        this.activeElement = null;
      }
    },
    openSettings() {
      this.activeElementIndex = null;
      this.activeElement = null;
    },
    resetAddDialogState() {
      this.selectedNewElement = 'HtmlScreen';
    },
    disableOutsideClick() {
      this.$emit('disableOutsideClick');
    },
    enableOutsideClick(ev) {
      this.$emit('enableOutsideClick', ev);
    }
  }
};

const moveElement = (array, oldIndex, newIndex) => {
  if (newIndex >= array.length) {
    var k = newIndex - array.length + 1;
    while (k--) {
      array.push(undefined);
    }
  }
  array.splice(newIndex, 0, array.splice(oldIndex, 1)[0]);
  return array;
};
</script>

<style lang="scss">
.lesson-edit-manager__top-right-btn {
  right: 4px !important;
  top: 4px;
  z-index: 4;
}
</style>
